import { useEffect, useRef } from 'react';

import { AnalyticsEventName, AnalyticsEventProps } from './events';
import { trackEvent } from './track-event';

/**
 * Отправляет событие один раз за жизнь компонента — когда `enabled` впервые станет `true`.
 *
 * Зачем: на дев-сервере `StrictMode` (`src/index.tsx`) вызывает эффекты дважды, и голый `useEffect` с `trackEvent`
 * слал в локальный проект Amplitude, по которому идут ручные проверки, два события на один заход. В продовой сборке
 * двойного вызова нет (https://react.dev/reference/react/StrictMode — только в разработке). Флаг в `useRef` переживает
 * этот двойной вызов: React сохраняет ref между симулированным размонтированием и повторным монтированием. Настоящее
 * перемонтирование (уход со страницы, переход `MobileGuard` через 900 px) — новый показ и новое событие.
 *
 * Свойства берутся на момент отправки; их смена позже второго события не даёт.
 *
 * Импортировать из этого файла, а не из `shared/analytics`: тесты мокают модуль `shared/analytics` целиком, и мок
 * стёр бы хук.
 */
export function useTrackEventOnce<N extends AnalyticsEventName>(name: N, props: AnalyticsEventProps[N], enabled = true): void {
  const sent = useRef(false);
  const latestProps = useRef(props);
  latestProps.current = props;

  useEffect(() => {
    if (!enabled || sent.current) {
      return;
    }

    sent.current = true;
    trackEvent(name, latestProps.current);
  }, [enabled, name]);
}
